/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import React from 'react';
import { SdsDivProps } from '../Component.props';
import { CHIP_ICON_LEFT, ChipIconPosition } from './types/ChipIcon.types';
import { ChipState } from './types/ChipState.types';
import { CHIP_THEME_DEFAULT, ChipTheme } from './types/ChipTheme.types';
import { ChipType } from './types/ChipType.types';

/**
 * The props for `<Chip />`.
 */
export interface ChipProps<TTextType extends React.ReactNode = React.ReactNode> extends SdsDivProps {
  /**
   * Navigation Chip click event handler.
   */
  customClick?(event: React.MouseEvent): void;

  /**
   * Selected state.
   */
  isSelected?: boolean;

  /**
   * Chip key identifier.
   */
  key: string;

  /**
   * The chip's current state.
   */
  state?: ChipState;

  /**
   * Chip text.
   */
  text: TTextType;

  /**
   * Label text for removing a chip.
   */
  toggleTextRemove?: string;

  /**
   * Label text for selecting a chip.
   */
  toggleTextSelect?: string;

  /**
   * Label text for deselecting a chip.
   */
  toggleTextDeselect?: string;

  /**
   * Theme of chip.
   */
  theme?: ChipTheme;

  /**
   * Type of chip.
   */
  type: ChipType;

  /**
   * Chip icon.
   */
  icon?: JSX.Element;

  /**
   * Chip icon colour from the sds colour map eg 'grey-300'.
   */
  iconColour?: string;

  /**
   * Which side of the button the icon is on.
   */
  iconPosition?: ChipIconPosition;
}

/**
 * The default prop values for `<Chip />`.
 */
export const chipDefaults = {
  iconPosition: CHIP_ICON_LEFT,
  isSelected: false,
  theme: CHIP_THEME_DEFAULT,
  toggleTextDeselect: `Press Enter or Space to deselect this option`,
  toggleTextRemove: `Press Delete to remove this option`,
  toggleTextSelect: `Press Enter or Space to select this option`,
};
